import "./ChangeRequestsPage.css";

import { ChangeRequestDetail, authorLabel, shortPerson, stateLabel } from "../components/ChangeRequestDetail";
import { ChangesetDTO, SpacePageDTO } from "../models/ChangeRequestDTO";
import { IChangeRequestService, changeRequestService, dispatchChangeRequestsChanged, errorMessage } from "../services/ChangeRequestService";
import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import { DateTimeHelper } from "../../../lib/utils/DateTimeHelper";
import { ILockService } from "../../locks/services/LockService";
import { Loader } from "../../../lib/components/loader/Loader";
import { PageContainer } from "../../../lib/layouts/containers/page/PageContainer";
import { PushPolicySelector } from "../components/PushPolicySelector";
import { useSpaceLocks } from "../../locks/hooks/useSpaceLocks";

const stateOptions = ["proposed", "approved", "applied", "rejected", "withdrawn", "failed", "all"];
const PageSize = 200;

interface IChangeRequestsPageProps {
    /** Injected in tests; the shared facade client otherwise. */
    service?: IChangeRequestService;
    /** Injected in tests; the shared lock client otherwise (lock badges on the changed pages). */
    lockService?: ILockService;
}

/**
 * Change requests of a space (`/spaces/:spaceId/change-requests`): changesets parked by the `propose` push policy or
 * sent with `orbital ark propose`, reviewed here (approve / reject with a reason / withdraw by the author).
 * `?cr=<id>` opens one for review and `?page=<key>` picks the changed page shown, so a review can be linked to.
 */
export function ChangeRequestsPage(props: IChangeRequestsPageProps) {
    const { spaceId } = useParams();
    const service = props.service ?? changeRequestService();
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedId = searchParams.get("cr") ?? undefined;
    const selectedItemKey = searchParams.get("page") ?? undefined;

    const [state, setState] = useState("proposed");
    const [items, setItems] = useState<ChangesetDTO[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | undefined>();
    const [pages, setPages] = useState<SpacePageDTO[]>([]);
    const [currentUserId, setCurrentUserId] = useState<string | undefined>();
    const { marks: lockMarks } = useSpaceLocks(spaceId, props.lockService);

    const load = useCallback(() => {
        if (!spaceId) return;
        setIsLoading(true);
        setError(undefined);
        service
            .list(spaceId, state, PageSize)
            .then((res) => {
                setItems(res);
                setIsLoading(false);
            })
            .catch((err) => {
                setError(errorMessage(err));
                setIsLoading(false);
            });
    }, [service, spaceId, state]);

    const loadPages = useCallback(() => {
        if (!spaceId) return;
        service.getPages(spaceId).then(setPages).catch(() => { /* changes fall back to ids, no stale check */ });
    }, [service, spaceId]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        if (!spaceId) return;
        let alive = true;
        loadPages();
        service.getCurrentUserId().then((id) => alive && setCurrentUserId(id)).catch(() => { /* no withdraw button */ });
        return () => {
            alive = false;
        };
    }, [service, spaceId, loadPages]);

    const handleChanged = useCallback(() => {
        load();
        loadPages();
        dispatchChangeRequestsChanged();
    }, [load, loadPages]);

    const open = useCallback((id: string) => setSearchParams({ cr: id }), [setSearchParams]);
    const close = useCallback(() => setSearchParams({}), [setSearchParams]);
    const selectItem = useCallback((key: string) => {
        if (selectedId) setSearchParams({ cr: selectedId, page: key }, { replace: true });
    }, [selectedId, setSearchParams]);

    return (
        <PageContainer className="change-requests-page">
            {!selectedId ? (
                <div className="change-requests-header">
                    <h1>Change requests</h1>
                    {spaceId ? <PushPolicySelector spaceId={spaceId} service={service} /> : null}
                </div>
            ) : null}

            {selectedId ? (
                <ChangeRequestDetail
                    key={selectedId}
                    changesetId={selectedId}
                    service={service}
                    spaceId={spaceId}
                    pages={pages}
                    locks={lockMarks}
                    currentUserId={currentUserId}
                    selectedItemKey={selectedItemKey}
                    onSelectItem={selectItem}
                    onChanged={handleChanged}
                    onClose={close}
                />
            ) : (
                <>
                    <div className="change-requests-filter">
                        <label htmlFor="change-requests-state">State</label>
                        <select id="change-requests-state" value={state} onChange={(ev) => setState(ev.target.value)}>
                            {stateOptions.map((s) => (
                                <option key={s} value={s}>{s === "all" ? "All" : stateLabel(s)}</option>
                            ))}
                        </select>
                    </div>

                    <div className="change-requests-list">
                        {error ? <div className="change-request-error">{error}</div> : null}
                        {isLoading && items.length === 0 ? <Loader /> : null}
                        {!isLoading && !error && items.length === 0 ? (
                            <div className="change-requests-empty">
                                No change requests {state === "all" ? "in this space" : `in state "${stateLabel(state).toLowerCase()}"`}.
                            </div>
                        ) : null}
                        {items.length > 0 ? (
                            <table className="change-requests-table">
                                <thead>
                                    <tr>
                                        <th>Change request</th>
                                        <th>Author</th>
                                        <th>Created</th>
                                        <th className="cell-ops">Changes</th>
                                        <th>State</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((cs) => {
                                        const author = authorLabel(cs.authorId, currentUserId);
                                        return (
                                            <tr key={cs.id} tabIndex={0}
                                                onClick={() => open(cs.id)}
                                                onKeyDown={(ev) => ev.key === "Enter" && open(cs.id)}>
                                                <td className="cell-message">{cs.message || "(no message)"}</td>
                                                <td className="cell-author" title={cs.authorId ?? undefined}>{shortPerson(author)}</td>
                                                <td className="cell-created" title={DateTimeHelper.formatDateTime(cs.createdAt)}>
                                                    {DateTimeHelper.formatDateTimeAsTimeAgo(cs.createdAt)}
                                                </td>
                                                <td className="cell-ops">{cs.opCount}</td>
                                                <td><span className={`change-request-state state-${cs.state}`}>{stateLabel(cs.state)}</span></td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        ) : null}
                    </div>
                </>
            )}
        </PageContainer>
    );
}
